// =============================================================================
// MeuDriver — Hook: useWallet
// Carteira de créditos do aluno: saldo, valor travado em aulas agendadas,
// extrato e recarga por Pix.
//
// Regras (decisões do dono, 2026-10-10 — migration 20261011):
//   * Saldo vale para qualquer instrutor (aula avulsa, experimental, pacote).
//   * Ao agendar, o valor da aula fica travado; é consumido quando a aula é
//     dada (ou o aluno falta / cancela com menos de 2 h) e volta ao saldo
//     quando o instrutor cancela ou o aluno cancela com 2 h ou mais.
//   * Saldo não é reembolsável — só vira aula.
// Toda movimentação acontece no servidor; aqui só se lê e se pede recarga.
// =============================================================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'

export const MIN_TOPUP = 10
export const MAX_TOPUP = 5000

export type WalletTransactionKind =
  | 'topup' | 'hold' | 'release' | 'package_purchase' | 'refund' | 'chargeback' | 'adjustment'

export interface WalletTransaction {
  id: string
  kind: WalletTransactionKind
  amount: number
  balance_after: number
  booking_id: string | null
  student_package_id: string | null
  description: string | null
  created_at: string
}

export const WALLET_TX_LABELS: Record<WalletTransactionKind, string> = {
  topup: 'Recarga de saldo',
  hold: 'Aula agendada',
  release: 'Aula cancelada — valor devolvido',
  package_purchase: 'Compra de pacote',
  refund: 'Devolução pelo suporte',
  chargeback: 'Estorno da recarga',
  adjustment: 'Ajuste',
}

export const walletKeys = {
  balance: (uid: string) => ['wallet', 'balance', uid] as const,
  held: (uid: string) => ['wallet', 'held', uid] as const,
  transactions: (uid: string) => ['wallet', 'transactions', uid] as const,
}

/** Saldo disponível (já descontado o que está travado em aulas). */
export function useWalletBalance() {
  const { user } = useAuth()
  return useQuery<number>({
    queryKey: walletKeys.balance(user?.id ?? ''),
    enabled: !!user?.id,
    staleTime: 1000 * 30,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('wallets')
        .select('balance')
        .eq('user_id', user!.id)
        .maybeSingle()
      if (error) throw error
      return Number((data as { balance?: number } | null)?.balance ?? 0)
    },
  })
}

/** Valor travado em aulas confirmadas que ainda não aconteceram. */
export function useWalletHeld() {
  const { user } = useAuth()
  return useQuery<number>({
    queryKey: walletKeys.held(user?.id ?? ''),
    enabled: !!user?.id,
    staleTime: 1000 * 30,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bookings')
        .select('wallet_amount')
        .eq('student_id', user!.id)
        .in('status', ['confirmed', 'in_progress'])
        .is('settled_at', null)
        .gt('wallet_amount', 0)
      if (error) throw error
      return ((data ?? []) as unknown as Array<{ wallet_amount: number }>).reduce((s, b) => s + Number(b.wallet_amount), 0)
    },
  })
}

export function useWalletTransactions(limit = 50) {
  const { user } = useAuth()
  return useQuery<WalletTransaction[]>({
    queryKey: [...walletKeys.transactions(user?.id ?? ''), limit],
    enabled: !!user?.id,
    staleTime: 1000 * 30,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('wallet_transactions')
        .select('id, kind, amount, balance_after, booking_id, student_package_id, description, created_at')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(limit)
      if (error) throw error
      return (data ?? []).map((t) => ({
        ...(t as WalletTransaction),
        amount: Number((t as WalletTransaction).amount),
        balance_after: Number((t as WalletTransaction).balance_after),
      }))
    },
  })
}

/** Gera o Pix de recarga e leva à tela de pagamento. */
export type TopupMethod = 'pix' | 'card'

export function useWalletTopup() {
  return useMutation({
    mutationFn: async ({ amount, method = 'pix' }: { amount: number; method?: TopupMethod }) => {
      const { data, error } = await supabase.functions.invoke('wallet-topup', { body: { amount, method } })
      if (error) {
        let msg = method === 'card' ? 'Não foi possível abrir o pagamento com cartão.' : 'Não foi possível gerar o Pix.'
        try {
          const ctx = (error as { context?: Response }).context
          const body = ctx ? await ctx.json() : null
          if (body?.error === 'invalid_amount') msg = `Escolha um valor entre R$ ${MIN_TOPUP} e R$ ${MAX_TOPUP}.`
          else if (body?.error === 'card_unavailable') msg = 'Pagamento com cartão indisponível no momento. Use o Pix.'
          else if (body?.message) msg = body.message
        } catch { /* mantém a mensagem genérica */ }
        throw new Error(msg)
      }
      return data as { charge_id: string; payment_url?: string; checkout_url?: string; amount: number }
    },
    onSuccess: (data) => {
      // Pix: nossa tela de QR Code. Cartão: checkout do Asaas (volta para a carteira).
      window.location.assign(data.checkout_url ?? data.payment_url ?? '/aluno/carteira')
    },
    onError: (err: Error) => toast.error(err.message),
  })
}

// ─── Pontos de fidelidade → saldo (100 pontos = R$ 1,00) ────────────────────

export const POINTS_PER_REAL = 100

export function useRedeemPointsToWallet() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (points: number) => {
      const { data, error } = await supabase.rpc('redeem_loyalty_to_wallet', { p_points: points })
      if (error) throw new Error(error.message.replace(/^.*?:\s*/, '') || 'Não foi possível trocar os pontos.')
      return Number(data ?? 0)
    },
    onSuccess: (credit) => {
      toast.success(`R$ ${credit.toFixed(2).replace('.', ',')} adicionados ao seu saldo.`)
      qc.invalidateQueries({ queryKey: ['wallet'] })
      qc.invalidateQueries({ queryKey: ['loyalty-points'] })
      qc.invalidateQueries({ queryKey: ['loyalty-history'] })
    },
    onError: (err: Error) => toast.error(err.message),
  })
}
